import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { cn } from '@/utils/cn';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
    fetchMenuBySlug,
    MenuOffer,
    getMenuGroups,
    isMenuSelectionComplete,
    getMenuDiscountedPrice,
    isMenuFixed,
} from '@/data/menus';
import { menuItems, formatPortionLabel } from '@/data/foods';
import { menuPage, getPageSeo, cartLabels } from '@/data/content';
import MenuGroupSelector from '@/components/ui/MenuGroupSelector';
import { useCart, buildCartOrders } from '@/Context/CartContext';
import { trackEvent } from '@/Services/analytics';
import api from '@/Services/api';
import { API } from '@/Services/apiConfig';
import { type ResolvedSlot, type FoodUpdate, type AvailabilityDay } from '@/types/menu';
import Button from '@/components/ui/Button';
import { SpinnerIcon } from '@/components/ui/Icons';
import { formatPrice } from '@/Services/format';

type SelectedItems = Map<number, number[]>;

interface StoredMenuFilter {
    date: string;
}

const resolveDefaultDay = (days: AvailabilityDay[]) => days.find((day) => day.foodCount > 0) ?? days[0] ?? null;

const MenuDetailPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const seo = getPageSeo('menu-detail');
    const { addToCart, cartItems, toggleCart } = useCart();
    const dateParam = searchParams.get('date');

    const [menu, setMenu] = useState<MenuOffer | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedItems, setSelectedItems] = useState<SelectedItems>(new Map());
    const [resolvedSlot, setResolvedSlot] = useState<ResolvedSlot | null>(null);
    const [foodUpdates, setFoodUpdates] = useState<Map<number, FoodUpdate>>(new Map());
    const [slotReady, setSlotReady] = useState(false);
    const menuGroups = useMemo(() => (menu ? getMenuGroups(menu) : []), [menu]);

    const saveStoredFilter = (value: StoredMenuFilter | null) => {
        try {
            if (value && value.date) {
                window.localStorage.setItem('selectedServiceDate', value.date);
            } else {
                window.localStorage.removeItem('selectedServiceDate');
            }
        } catch {
            /* ignore */
        }
    };

    useEffect(() => {
        const loadMenu = async () => {
            if (!slug) {
                setLoading(false);
                return;
            }

            const found = await fetchMenuBySlug(slug);
            if (found) {
                setMenu(found);
                setSelectedItems(new Map());
            } else {
                setError(menuPage.coming_soon);
            }
            setLoading(false);
        };

        loadMenu();
    }, [slug]);

    useEffect(() => {
        if (!menu) return;
        trackEvent('menu_view', { menu_id: menu.id, menu_name: menu.name });
    }, [menu]);

    const fetchAvailability = useCallback(async (): Promise<AvailabilityDay[]> => {
        const { data } = await api.get(API.MENU_AVAILABILITY);
        const days: AvailabilityDay[] = data?.days ?? [];
        return days;
    }, []);

    const fetchSlotOverlay = useCallback(
        async (date?: string) => {
            if (!date) return;

            const body: Record<string, unknown> = {
                cartOrders: buildCartOrders(cartItems),
                date,
            };

            const { data } = await api.post(API.MENU, body);
            const updates: FoodUpdate[] = data?.food_updates ?? [];
            const updateMap = new Map<number, FoodUpdate>();

            updates.forEach((update) => {
                updateMap.set(update.id, update);
            });

            setFoodUpdates(updateMap);
            setResolvedSlot({
                date: data?.slot?.date ?? date,
                period: data?.slot?.period ?? '',
                fulfilment: data?.slot?.fulfilment ?? '',
                slotWeight: data?.slot?.slotWeight ?? 0,
            });
            setSlotReady(true);
        },
        [cartItems]
    );

    useEffect(() => {
        if (!menu) return;

        const initSlot = async () => {
            const days = await fetchAvailability();

            let resolvedDate: string | undefined;

            try {
                const date = localStorage.getItem('selectedServiceDate');
                if (date) {
                    const day = days.find((d) => d.date === date);
                    if (day) {
                        resolvedDate = date;
                    }
                }
            } catch {
                /* ignore */
            }

            if (!resolvedDate) {
                const defaultDay = resolveDefaultDay(days);
                resolvedDate = defaultDay?.date;
            }

            if (!resolvedDate) return;

            saveStoredFilter({ date: resolvedDate });
            await fetchSlotOverlay(resolvedDate);
        };

        initSlot();
    }, [menu, dateParam, fetchAvailability, fetchSlotOverlay, setSearchParams]);

    useEffect(() => {
        if (!menu || !slotReady) return;

        setSelectedItems((prev) => {
            const next = new Map(prev);

            menuGroups.forEach((group) => {
                const availableIds = group.foods
                    .map((food) => food.id)
                    .filter((foodId) => foodUpdates.has(foodId))
                    .sort((a, b) => {
                        const weightA = foodUpdates.get(a)?.weight_percentage ?? 0;
                        const weightB = foodUpdates.get(b)?.weight_percentage ?? 0;
                        return weightA - weightB;
                    });

                const currentIds = next.get(group.id) ?? [];
                const stillValidIds = currentIds.filter((foodId) => availableIds.includes(foodId));

                if (group.pick === 1) {
                    const replacement = stillValidIds[0] ?? availableIds[0];
                    next.set(group.id, replacement ? [replacement] : []);
                    return;
                }

                const toppedUpIds = [
                    ...stillValidIds,
                    ...availableIds.filter((foodId) => !stillValidIds.includes(foodId)),
                ].slice(0, group.pick);

                next.set(group.id, toppedUpIds);
            });

            return next;
        });
    }, [menuGroups, slotReady, foodUpdates]);

    const handleSelectionChange = (groupId: number, foodIds: number[]) => {
        setSelectedItems((prev) => {
            const next = new Map(prev);
            next.set(groupId, foodIds);
            return next;
        });
    };

    const isComplete = menu ? isMenuSelectionComplete(menu, selectedItems) : false;

    const discountedTotal = useMemo(() => {
        if (!menu) return 0;
        return getMenuDiscountedPrice(menu, selectedItems);
    }, [menu, selectedItems]);

    const completedGroups = useMemo(() => {
        return menuGroups.filter((g) => (selectedItems.get(g.id) ?? []).length >= g.pick).length;
    }, [menuGroups, selectedItems]);

    const totalGroups = menuGroups.length;

    const selectionRecap = useMemo(() => {
        const recap: { groupName: string; foods: string[] }[] = [];
        menuGroups.forEach((group) => {
            const ids = selectedItems.get(group.id) ?? [];
            if (ids.length === 0) return;
            const names = ids.map((fid) => menuItems.find((f) => f.id === fid)?.item_name).filter(Boolean) as string[];
            recap.push({ groupName: group.name, foods: names });
        });
        return recap;
    }, [menuGroups, selectedItems]);

    const handleAddToCart = () => {
        if (!menu || !isComplete) return;

        const instanceId = Date.now().toString();
        const discount = menu.discount_percentage / 100;
        const totalMenuPrice = Math.round(Number(menu.price) * (1 - discount) * 100) / 100;
        let isFirstItem = true;

        menuGroups.forEach((group) => {
            const ids = selectedItems.get(group.id) ?? [];
            ids.forEach((foodId) => {
                const food = menuItems.find((f) => f.id === foodId);
                if (food) {
                    const groupFood = group.foods.find((item) => item.id === foodId);
                    const basePrice = Number(groupFood?.price ?? food.price ?? 0);

                    let itemPrice = Math.round(basePrice * (1 - discount) * 100) / 100;
                    if (Number(menu.price) > 0) {
                        itemPrice = isFirstItem ? totalMenuPrice : 0;
                        isFirstItem = false;
                    }

                    const portionLabel = formatPortionLabel(
                        groupFood?.portion ?? food.portion_quantity,
                        'g'
                    );

                    addToCart(
                        {
                            id: food.id,
                            name: food.item_name,
                            item_name: food.item_name,
                            price: itemPrice,
                            weight_percentage: 0,
                            menu_id: menu.id,
                            menu_name: menu.name,
                            menu_instance_id: instanceId,
                            menu_group_name: group.name,
                            portion_label: portionLabel,
                            date: resolvedSlot?.date,
                            period: resolvedSlot?.period,
                            fulfilment: resolvedSlot?.fulfilment,
                        },
                        1
                    );
                }
            });
        });

        toggleCart();
    };

    const remainingCapacity = resolvedSlot ? 100 - resolvedSlot.slotWeight : 100;
    const menuHeroFoods = useMemo(() => {
        return menuGroups
            .flatMap((group) =>
                group.foods.map((groupFood) => {
                    const foodItem = menuItems.find((food) => food.id === groupFood.id);
                    return foodItem?.image_path
                        ? {
                              id: groupFood.id,
                              name: foodItem.item_name,
                              image_path: foodItem.image_path,
                          }
                        : null;
                })
            )
            .filter(Boolean)
            .filter(
                (food, index, foods) =>
                    foods.findIndex((candidate) => candidate?.id === food?.id) === index
            )
            .slice(0, 4) as { id: number; name: string; image_path: string }[];
    }, [menuGroups]);

    const renderMenuHero = () => {
        if (menuHeroFoods.length === 0 && menu?.image_path) {
            return (
                <img
                    src={menu.image_path.startsWith('/') ? menu.image_path : `/${menu.image_path}`}
                    alt={menu.name}
                    className="w-full h-full object-cover absolute inset-0"
                />
            );
        }

        return (
            <div
                className={cn(
                    'grid gap-3 sm:gap-4 w-full h-full',
                    menuHeroFoods.length === 1 ? 'grid-cols-1' : menuHeroFoods.length === 2 ? 'grid-cols-2' : 'grid-cols-2 grid-rows-2'
                )}
            >
                {menuHeroFoods.map((food) => (
                    <div
                        key={food.id}
                        className="relative w-full h-full flex items-center justify-center overflow-hidden rounded-2xl bg-white/60 dark:bg-gray-800/60 shadow-sm p-3 sm:p-4"
                    >
                        <img
                            src={food.image_path}
                            alt={food.name}
                            className="w-full h-full object-contain drop-shadow-md"
                        />
                    </div>
                ))}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <SpinnerIcon className="w-10 h-10 text-app-primary" />
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{menuPage.loading_details}</p>
                </div>
            </div>
        );
    }

    if (error || !menu) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-4">
                <div className="flex flex-col items-center text-center gap-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-8 max-w-sm shadow-sm">
                    <h2 className="text-lg font-bold text-app-text dark:text-white mb-1">{menuPage.load_error}</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{error || menuPage.not_found}</p>
                    <Button variant="primary" size="md" onClick={() => navigate('/menu')}>
                        {menuPage.back_to_menu}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{menu.name} | Bivin's Kitchen</title>
            </Helmet>
            <div className="menu-detail-page max-w-3xl mx-auto px-4 py-6 pb-28">
            <h1 className="sr-only">{seo?.h1 ?? menu.name}</h1>

            <Button
                variant="ghost"
                type="button"
                onClick={() => navigate('/menu')}
                className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-app-primary transition-colors mb-4"
            >
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                {menuPage.back_to_menu}
            </Button>

            <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 mb-6">
                <span>Menu</span>
                <span>&#8250;</span>
                <span className="text-app-text dark:text-white font-medium">{menu.name}</span>
            </div>

            <div className="bg-app-surface rounded-[2rem] shadow-sm border border-app-border overflow-hidden">
                {(menu.image_path || menuHeroFoods.length > 0) && (
                    <div
                        className={cn('relative aspect-[4/3] bg-app-bg overflow-hidden', isMenuFixed(menu) && 'p-4 sm:p-6')}
                    >
                        {menuHeroFoods.length > 0 && (!menu.image_path || isMenuFixed(menu)) ? (
                            renderMenuHero()
                        ) : (
                            <img
                                src={menu.image_path.startsWith('/') ? menu.image_path : `/${menu.image_path}`}
                                alt={menu.name}
                                className="w-full h-full object-cover absolute inset-0"
                            />
                        )}
                        {menu.discount_percentage > 0 && (
                            <div className="absolute top-3 right-3 bg-red-500 text-white text-sm font-black px-3 py-1.5 rounded-xl shadow-md z-10">
                                -{menu.discount_percentage}%
                            </div>
                        )}
                    </div>
                )}

                <div className="p-5 sm:p-6 space-y-5">
                    <div>
                        <h2 className="text-xl font-black text-app-text">{menu.name}</h2>
                        {menu.description && <p className="text-sm text-app-muted mt-1">{menu.description}</p>}
                    </div>

                    {menuGroups.map((group) => {
                        const groupFoods = group.foods
                            .map((groupFood) => {
                                const food = menuItems.find((f) => f.id === groupFood.id);
                                return food ? { source: food, groupFood } : null;
                            })
                            .filter(Boolean)
                            .map((entry) => {
                                const food = entry!.source;
                                const groupFood = entry!.groupFood;
                                const update = foodUpdates.get(food.id);
                                const menuPortionLabel = formatPortionLabel(
                                    groupFood.portion ?? food.portion_quantity,
                                    'g'
                                );

                                return {
                                    id: food.id,
                                    item_name: food.item_name,
                                    price: Number(groupFood.price ?? food.price ?? 0),
                                    image_path: food.image_path,
                                    food_unit: 'g',
                                    portion_label: menuPortionLabel,
                                    weight_percentage: update?.weight_percentage ?? 0,
                                    daily_capacity: slotReady ? (update?.daily_capacity ?? 0) : 0,
                                };
                            }) as {
                            id: number;
                            item_name: string;
                            price: number;
                            image_path?: string;
                            food_unit?: string;
                            portion_label?: string;
                            weight_percentage?: number;
                            daily_capacity?: number;
                        }[];

                        const groupPortionLabel = groupFoods[0]?.portion_label ?? '';

                        return (
                            <MenuGroupSelector
                                key={group.id}
                                groupName={group.name}
                                pick={group.pick}
                                portionLabel={groupPortionLabel}
                                options={groupFoods.map((f) => ({
                                    id: f.id,
                                    name: f.item_name,
                                    image_path: f.image_path,
                                    price: f.price,
                                    portion_label: f.portion_label,
                                    weight_percentage: f.weight_percentage,
                                    daily_capacity: f.daily_capacity,
                                }))}
                                selectedIds={selectedItems.get(group.id) ?? []}
                                onSelectionChange={(ids) => handleSelectionChange(group.id, ids)}
                                    labels={{
                                        outOfStock: menuPage.out_of_stock,
                                        pickLabel: (pick, portionLabel) => {
                                            const label = portionLabel || (pick === 1 ? menuPage.option_label : menuPage.options_label);
                                            if (pick === 1) return menuPage.pick_one_label.replace('{label}', label);
                                            return menuPage.pick_multiple_label.replace('{count}', String(pick)).replace('{label}', label);
                                        },
                                    }}
                            />
                        );
                    })}
                </div>

                {selectionRecap.length > 0 && (
                    <div className="px-5 sm:px-6 pb-5">
                        <div className="bg-app-bg rounded-xl border border-app-border p-4 space-y-2">
                            <h4 className="text-xs font-bold text-app-muted uppercase tracking-wider">
                                {menuPage.selection_recap_heading}
                            </h4>
                            {selectionRecap.map((item) => (
                                <div key={item.groupName} className="text-sm">
                                    <span className="font-semibold text-app-text">{item.groupName}: </span>
                                    <span className="text-app-muted">{item.foods.join(', ')}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {slotReady && (
                <div className="fixed bottom-0 left-0 right-0 bg-app-surface/95 backdrop-blur-lg border-t border-app-border shadow-2xl z-50">
                    <div className="max-w-3xl mx-auto px-4 py-3 sm:py-4">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="flex items-center gap-1.5">
                                {menuGroups.map((group) => {
                                    const count = (selectedItems.get(group.id) ?? []).length;
                                    const done = count >= group.pick;
                                    return (
                                        <div
                                            key={group.id}
                                            className={cn(
                                                'w-2.5 h-2.5 rounded-full transition-all duration-300',
                                                done
                                                    ? 'bg-green-500 shadow-sm shadow-green-500/40'
                                                    : count > 0
                                                      ? 'bg-amber-400'
                                                      : 'bg-gray-300 dark:bg-gray-600'
                                            )}
                                            title={`${group.name}: ${count}/${group.pick}`}
                                        />
                                    );
                                })}
                            </div>
                            <span className="text-[11px] font-medium text-app-muted">
                                {completedGroups}/{totalGroups} {menuPage.group_progress_label}
                            </span>
                            {remainingCapacity <= 20 && remainingCapacity > 0 && (
                                <span className="text-[11px] font-semibold text-amber-600 ml-auto">
                                    {menuPage.availability_remaining_label.replace('{percent}', String(remainingCapacity))}
                                </span>
                            )}
                            {remainingCapacity <= 0 && (
                                <span className="text-[11px] font-semibold text-red-500 ml-auto">{menuPage.availability_full_label}</span>
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                                {discountedTotal > 0 && (
                                    <div className="flex items-baseline gap-1.5">
                                        <span className="text-xl font-black text-app-text">
                                            {formatPrice(discountedTotal)}
                                        </span>
                                        {menu.discount_percentage > 0 && (
                                            <span className="text-xs text-app-muted line-through">
                                                {formatPrice(discountedTotal / (1 - menu.discount_percentage / 100))}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleAddToCart}
                                disabled={!isComplete || remainingCapacity <= 0}
                                className="!py-0 h-9 !text-xs font-black uppercase tracking-tight shrink-0 gap-2"
                            >
                                <span className="hidden xl:inline">{cartLabels.add_button}</span>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                                    />
                                </svg>
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
        </>
    );
};

export default MenuDetailPage;
